import { action, makeObservable, observable, reaction } from 'mobx';
import { UiStore } from './ui';
import { appConfig } from '../settings';
import {
    ICardSetRevenueItem,
    ICardSetUsage,
    ICardsRevenueItem,
    ICardsUsage,
    ICompanyRevenueItem,
    ICompanyUsage,
    ICountryRevenueItem,
    ICountryUsage,
    IDashboardType,
    IFilterParams,
    IFinancesRollCards,
    IOperatorRevenueItem,
    IOverlayView,
    IParamsList,
    IStatItem,
    ITelecomUsage,
    ITimelineRevenueItem,
    ITimelineUsage,
    ITimelineZoneItem,
    IUsageActivityRoll,
    IUsageMap,
    IZoneUsage,
    SelectType,
} from '../interfaces';
import {
    UnitTypeValues,
    abortAllDashboardFetch,
    getDashboardActivityRoll,
    getDashboardCardSetRevenueList,
    getDashboardCardSetUsageList,
    getDashboardCardsRevenueList,
    getDashboardCardsUsageList,
    getDashboardCompanyRevenueList,
    getDashboardCompanyUsageList,
    getDashboardCountryRevenueList,
    getDashboardCountryUsageList,
    getDashboardFinancesUsage,
    getDashboardRelationSelect,
    getDashboardTelecomRevenueList,
    getDashboardTelecomUsageList,
    getDashboardTimelineCompanyRevenueList,
    getDashboardTimelineUsageList,
    getDashboardZoneRevenueList,
    getDashboardZoneUsageList,
    getFinanceRelationSelect,
    getStoreProfileSettings,
    getStoreVisibleUnitTypes,
} from '../middleware';
import { apiParamsCleaner } from '../instruments';

export class DashboardStore {
    private constructor() {
        makeObservable(this);
        reaction(
            () => this.forcedUpdateTime,
            () => {
                this.forcedUpdateReaction();
            },
            {
                delay: appConfig.APIDelay,
            },
        );
    }

    private static instance: DashboardStore;
    public static getInstance(): DashboardStore {
        if (!DashboardStore.instance) {
            DashboardStore.instance = new DashboardStore();
        }
        return DashboardStore.instance;
    }

    // ------------ Activity Usage ------------
    @observable usageActivity: IUsageActivityRoll = { costs: [], statsByUnitType: [] };
    @action setUsageActivity(data: IUsageActivityRoll): void {
        this.usageActivity = data;
    }

    // ------------ Finances Usage ------------
    @observable financesRollCards: IFinancesRollCards = { active: 0, inactive: 0 };
    @action setFinancesRollCards(data: IFinancesRollCards): void {
        this.financesRollCards = data;
    }
    @observable financesRollStats: Array<IStatItem> = [];
    @action setFinancesRollStats(data: Array<IStatItem>): void {
        this.financesRollStats = data;
    }
    @observable financesRollVolume = '0';
    @action setFinancesRollVolume(data: string): void {
        this.financesRollVolume = data;
    }

    // ------------ countryUsage ------------
    @observable countryUsageList: Array<ICountryUsage> = [];
    @action setCountryUsageList(data: Array<ICountryUsage>): void {
        this.countryUsageList = data;
    }

    @observable countryVoiceUsageList: Array<ICountryUsage> = [];
    @action setCountryVoiceUsageList(data: Array<ICountryUsage>): void {
        this.countryVoiceUsageList = data;
    }

    // ------------ Card Set Usage ------------
    @observable cardSetUsageList: Array<ICardSetUsage> = [];
    @action setCardSetUsageList(data: Array<ICardSetUsage>): void {
        this.cardSetUsageList = data;
    }

    @observable cardSetVoiceUsageList: Array<ICardSetUsage> = [];
    @action setCardSetVoiceUsageList(data: Array<ICardSetUsage>): void {
        this.cardSetVoiceUsageList = data;
    }

    // ------------ Cards Usage ------------
    @observable cardsUsageList: Array<ICardsUsage> = [];
    @action setCardsUsageList(data: Array<ICardsUsage>): void {
        this.cardsUsageList = data;
    }

    @observable cardsVoiceUsageList: Array<ICardsUsage> = [];
    @action setCardsVoiceUsageList(data: Array<ICardsUsage>): void {
        this.cardsVoiceUsageList = data;
    }

    // ------------ Timeline Usage ------------
    @observable timelineUsageList: Array<ITimelineUsage> = [];
    @action setTimelineUsageList(data: Array<ITimelineUsage>): void {
        this.timelineUsageList = data;
    }

    // ------------ companyUsage ------------
    @observable companyUsageList: Array<ICompanyUsage> = [];
    @action setCompanyUsageList(data: Array<ICompanyUsage>): void {
        this.companyUsageList = data;
    }

    // ------------ ZoneUsage ------------
    @observable zoneUsageList: Array<IZoneUsage> = [];
    @action setZoneUsageList(data: Array<IZoneUsage>): void {
        this.zoneUsageList = data;
    }

    // ------------ TelecomUsage ------------
    @observable telecomUsageList: Array<ITelecomUsage> = [];
    @action setTelecomUsageList(data: Array<ITelecomUsage>): void {
        this.telecomUsageList = data;
    }

    // ------------ CompaniesRevenue ------------
    @observable companyRevenueList: Array<ICompanyRevenueItem> = [];
    @action setCompaniesRevenueList(data: Array<ICompanyRevenueItem>): void {
        this.companyRevenueList = data;
    }

    // ------------ CardSet Revenue ------------
    @observable cardSetRevenueList: Array<ICardSetRevenueItem> = [];
    @action setCardSetRevenueList(data: Array<ICardSetRevenueItem>): void {
        this.cardSetRevenueList = data;
    }
    // ------------ Cards Revenue ------------
    @observable cardsRevenueList: Array<ICardsRevenueItem> = [];
    @action setCardsRevenueList(data: Array<ICardsRevenueItem>): void {
        this.cardsRevenueList = data;
    }

    // ------------ EditPurchaseZoneRate Revenue ------------
    @observable countryRevenueList: Array<ICountryRevenueItem> = [];
    @action setCountryRevenueList(data: Array<ICountryRevenueItem>): void {
        this.countryRevenueList = data;
    }

    // ------------ Zone Revenue ------------
    @observable zoneRevenueList: Array<ITimelineZoneItem> = [];
    @action setZoneRevenueList(data: Array<ITimelineZoneItem>): void {
        this.zoneRevenueList = data;
    }

    // ------------ Operator/Telecom Revenue ------------
    @observable telecomRevenueList: Array<IOperatorRevenueItem> = [];
    @action setTelecomRevenueList(data: Array<IOperatorRevenueItem>): void {
        this.telecomRevenueList = data;
    }

    // ------------ TimelineRevenue ------------
    @observable timelineRevenueList: Array<ITimelineRevenueItem> = [];
    @action setTimelineRevenueList(data: Array<ITimelineRevenueItem>): void {
        this.timelineRevenueList = data;
    }

    // ------------ filterParams ------------
    @observable filterParamsFinance: IFilterParams = {
        companyId: true,
        dateRange: true,
        cardSetId: true,
        operatorId: true,
        defaultDateRangeType: 'month',
        defaultUnitTypes: UnitTypeValues.DATA,
        isDateRangeCleanedDefaultURL: true,
        isDateRangeMaxDayToDay: true,
        isDateRangeMinDayOn: true,
        unitTypes: SelectType.SINGLE,
    };

    @observable filterParamsDashboard: IFilterParams = {
        dateRange: true,
    };

    // ------------ main Store ------------
    @observable dashboardType: IDashboardType = 'Main';
    @action setDashboardType(type: IDashboardType): void {
        this.dashboardType = type;
    }

    @observable forcedUpdateTime = 0;
    @action setForcedUpdateTime(): void {
        this.forcedUpdateTime = +Date.now();
    }

    @observable prevParams: IParamsList = {};

    async forcedUpdateReaction(): Promise<void> {
        const UiStoreInstance = UiStore.getInstance();
        const params = UiStoreInstance.params;

        abortAllDashboardFetch();

        switch (this.dashboardType) {
            case 'Main':
                const cleanDashboardParams = apiParamsCleaner(params, this.filterParamsDashboard);
                await getDashboardRelationSelect();
                getDashboardActivityRoll();
                getDashboardTimelineUsageList({});
                // ------------ Data and Voice, when the tenant may see them ------------
                const visibleUnitTypes = getStoreVisibleUnitTypes();
                [UnitTypeValues.DATA, UnitTypeValues.VOICE]
                    .filter((unitType) => visibleUnitTypes.includes(unitType))
                    .forEach((unitType) => {
                        getDashboardCountryUsageList({ ...cleanDashboardParams, unitType });
                        getDashboardCardSetUsageList({ ...cleanDashboardParams, unitType });
                        getDashboardCardsUsageList({ ...cleanDashboardParams, unitType });
                    });
                break;
            case 'Finances': {
                const { hideCosts } = getStoreProfileSettings();
                const cleanFinanceParams = apiParamsCleaner(params, this.filterParamsFinance);
                await getFinanceRelationSelect(params);
                getDashboardCompanyUsageList(cleanFinanceParams);
                !hideCosts && getDashboardCompanyRevenueList(cleanFinanceParams);
                getDashboardCardSetUsageList(cleanFinanceParams);
                !hideCosts && getDashboardCardSetRevenueList(cleanFinanceParams);
                getDashboardCountryUsageList(cleanFinanceParams);
                !hideCosts && getDashboardCountryRevenueList(cleanFinanceParams);
                getDashboardCardsUsageList(cleanFinanceParams);
                !hideCosts && getDashboardCardsRevenueList(cleanFinanceParams);
                getDashboardZoneUsageList(cleanFinanceParams);
                !hideCosts && getDashboardZoneRevenueList(cleanFinanceParams);
                getDashboardTelecomUsageList(cleanFinanceParams);
                !hideCosts && getDashboardTelecomRevenueList(cleanFinanceParams);
                getDashboardFinancesUsage(cleanFinanceParams);
                // if (isUpdateTimeLine) {
                getDashboardTimelineUsageList(cleanFinanceParams);
                !hideCosts && getDashboardTimelineCompanyRevenueList(cleanFinanceParams);
                // }
                break;
            }
            default:
                break;
        }
        this.prevParams = params;
    }

    @action cleanStore(): void {
        this.usageActivity = { costs: [], statsByUnitType: [] };
        this.cardSetRevenueList = [];
        this.cardSetUsageList = [];
        this.cardsRevenueList = [];
        this.cardsUsageList = [];
        this.companyRevenueList = [];
        this.companyUsageList = [];
        this.countryRevenueList = [];
        this.countryUsageList = [];
        this.mapOverlayView = null;
        this.prevParams = {};
        this.telecomRevenueList = [];
        this.telecomUsageList = [];
        this.timelineUsageList = [];
        this.zoneRevenueList = [];
        this.zoneUsageList = [];
        this.usageData.clear();
    }

    @observable usageData = new Map<string, IUsageMap>();
    @action setUsageData(dataName: string, value: IUsageMap): void {
        this.usageData.set(dataName, value);
    }

    @observable mapOverlayView: IOverlayView = null;

    @action setMapOverlayView(overlayView: IOverlayView): void {
        this.mapOverlayView = overlayView;
    }
}
